import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { usePathname, useRouter } from '@/i18n/navigation'
import { useSearchParams } from 'next/navigation'

import { FilterIcon } from '@/app/[locale]/_assets/icon-components'
import { Button } from '@/components/ui/button'
import { useLocale, useTranslations } from 'next-intl'
import {
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverHeader,
  PopoverRoot,
  PopoverTrigger,
} from '@/components/ui/popover'
import { Box, Flex, Text, Link, Float, Circle } from '@chakra-ui/react'
import Sorted from './components/Sorted'
import Tags from './components/Tags'

const FilterDesktop = () => {
  // State management
  const [sort, setSort] = useState('')
  const [open, setOpen] = useState(false)
  const [selectedTags, setSelectedTags] = useState<string[]>([])

  const router = useRouter()
  const t = useTranslations()
  const pathname = usePathname()
  const searchParams = useSearchParams()
  const currentLocale = useLocale()
  // Extract query parameters
  const tagIds = searchParams.get('tag_ids')
  const sortValue = searchParams.get('sort')

  // Memoize filter options to prevent unnecessary re-renders
  const filterOptions = useMemo(
    () => [
      {
        id: '1',
        label: t('filter_for_title'),
        value: `title.${currentLocale}|asc`,
      },
      {
        id: '2',
        label: t('filter_for_rate'),
        value: 'rate|asc',
      },
      {
        id: '3',
        label: t('filter_for_out_price_asc'),
        value: 'out_price|asc',
      },
      {
        id: '4',
        label: t('filter_for_out_price_desc'),
        value: 'out_price|desc',
      },
    ],
    [t]
  )

  // Get filter option ID by value
  const getIdByValue = useCallback(
    (value: string) => {
      const option = filterOptions.find((option) => option.value === value)
      return option ? option.id : '0'
    },
    [filterOptions]
  )

  // Handle sorting change
  const handleSortChange = useCallback((newSort: string | null) => {
    setSort(newSort ?? '')
  }, [])

  // Handle tags change
  const handleTagsChange = useCallback((tags: string[]) => {
    setSelectedTags(tags)
  }, [])

  // Clear all filters
  const handleClearFilter = useCallback(() => {
    setSelectedTags([])
    setSort('')
    router.push(pathname, { scroll: false })
    setOpen(false)
  }, [router, pathname])

  // Apply filters
  const handleApplyFilter = useCallback(() => {
    const queryParams = new URLSearchParams()

    if (selectedTags.length > 0) {
      queryParams.append('tag_ids', selectedTags.join(','))
    }

    if (sort) {
      queryParams.append('sort', sort)
    }

    const newPath = queryParams.toString()
      ? `${pathname}?${queryParams.toString()}`
      : pathname

    router.push(newPath, { scroll: false })
    setOpen(false)
  }, [selectedTags, sort, pathname, router])

  // Check if any filters are active
  const hasActiveFilters = useMemo(
    () => Boolean(tagIds || sortValue),
    [tagIds, sortValue]
  )

  // Sync state with URL parameters
  useEffect(() => {
    // Update tags from URL
    if (tagIds && tagIds.length > 0) {
      setSelectedTags(tagIds.split(','))
    } else {
      setSelectedTags([])
    }

    // Update sort from URL
    if (sortValue && +getIdByValue(sortValue) > 0) {
      setSort(sortValue)
    } else {
      setSort('')
    }
  }, [tagIds, sortValue, getIdByValue])

  return (
    <PopoverRoot
      open={open}
      lazyMount
      onOpenChange={(e) => setOpen(e.open)}
      positioning={{ placement: 'bottom-end' }}
    >
      <PopoverTrigger asChild>
        <Box position='relative'>
          <Button
            bg='background.card'
            color='text'
            rounded={16}
            size='lg'
            h={14}
          >
            <FilterIcon color='currentColor' /> {t('filters')}
          </Button>
          {hasActiveFilters && (
            <Float offsetY={1} offsetX={1}>
              <Circle size='5' bg='primary' color='white' />
            </Float>
          )}
        </Box>
      </PopoverTrigger>
      <PopoverContent bg='var(--bg-card)' color='var(--text-default)'>
        <PopoverHeader
          display='flex'
          alignItems='center'
          justifyContent='space-between'
        >
          <Text fontWeight={600} fontSize='lg' lineHeight='28px'>
            {t('sorting')}
          </Text>
          <Text
            color='primary'
            as={Link}
            fontWeight={400}
            onClick={handleClearFilter}
            cursor='pointer'
          >
            {t('reset')}
          </Text>
        </PopoverHeader>
        <PopoverBody pt={4} pb={3} pl={6} pr={3} overflow='auto'>
          <Sorted
            value={sort}
            options={filterOptions}
            onValueChange={handleSortChange}
          />

          <Tags values={selectedTags} onChange={handleTagsChange} />
        </PopoverBody>
        <PopoverFooter pt={3}>
          <Button
            rounded={16}
            size='xl'
            height='56px'
            bg='primary'
            color='primary.text'
            w='full'
            onClick={handleApplyFilter}
          >
            {t('to_filter')}
          </Button>
        </PopoverFooter>
      </PopoverContent>
    </PopoverRoot>
  )
}

export default FilterDesktop
